import { Component, EventEmitter, Input, Output } from '@angular/core';
import { Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { QueryStatus, QueryWithExecutionRecord } from '../../../../../API';
import { SegmentHistoryRecord } from '../../../models/history-model';
import { FanExplorationService } from '../../../services/fan-exploration.service';
import { QueryHistoryService } from '../../../services/query-history.service';

@Component({
  selector: 'app-segment-history',
  templateUrl: './segment-history.component.html',
  styleUrl: './segment-history.component.scss',
})
export class SegmentHistoryComponent {
  @Input() searchTerm = '';
  @Input() rows: SegmentHistoryRecord[] = [];
  @Output() deleteEvent: EventEmitter<any> = new EventEmitter<any>();

  headers: any[] = [];
  isViewModalVisible = false;
  isViewModalLoading = false;
  selectedRow: SegmentHistoryRecord | null = null;
  selectedQueryRecord: QueryWithExecutionRecord | null = null;
  selectedQueryText = '';

  constructor(
    private queryHistoryService: QueryHistoryService,
    private fanExplorationService: FanExplorationService,
    private messageService: NzMessageService,
    private router: Router,
  ) {
    this.setHeaders();
  }

  get filteredRows(): SegmentHistoryRecord[] {
    return this.rows.filter((row) => this.matchesSearch(row));
  }

  setHeaders(): void {
    this.headers = [
      {
        name: 'Query ID',
        width: '14rem',
        sortOrder: null,
        sortFn: (a: SegmentHistoryRecord, b: SegmentHistoryRecord) => a.queryId.localeCompare(b.queryId),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Segment Name',
        width: '14rem',
        sortOrder: null,
        sortFn: (a: SegmentHistoryRecord, b: SegmentHistoryRecord) => a.segmentName.localeCompare(b.segmentName),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Segment Description',
        width: '24rem',
        sortOrder: null,
        sortFn: (a: SegmentHistoryRecord, b: SegmentHistoryRecord) =>
          a.segmentDescription.localeCompare(b.segmentDescription),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Status',
        width: '8.75rem',
        sortOrder: null,
        sortFn: (a: SegmentHistoryRecord, b: SegmentHistoryRecord) => a.status.localeCompare(b.status),
        filterMultiple: true,
        filters: [
          { text: 'DRAFT', value: 'DRAFT' },
          { text: 'SUCCESS', value: 'EXECUTED' },
          { text: 'FAILED', value: 'FAILED' },
        ],
        filterFn: (selectedStatuses: QueryStatus[], item: SegmentHistoryRecord) =>
          !selectedStatuses.length || selectedStatuses.includes(item.status),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Segmented Fans',
        width: '10rem',
        sortOrder: null,
        sortFn: (a: SegmentHistoryRecord, b: SegmentHistoryRecord) => a.segmentedFans - b.segmentedFans,
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Created On',
        width: '12rem',
        sortOrder: 'descend',
        sortFn: (a: SegmentHistoryRecord, b: SegmentHistoryRecord) =>
          new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Created By',
        width: '12rem',
        sortOrder: null,
        sortFn: (a: SegmentHistoryRecord, b: SegmentHistoryRecord) => a.createdBy.localeCompare(b.createdBy),
        sortDirections: ['ascend', 'descend', null],
      },
      {
        name: 'Action',
        width: '6rem',
        right: '0',
        sortOrder: null,
        sortFn: null,
        sortDirections: [],
      },
    ];
  }

  trackByHeader(index: number, item: { name: string }): string {
    return item.name;
  }

  isEditDisabled(row: SegmentHistoryRecord): boolean {
    return row.status === QueryStatus.EXECUTED;
  }

  async onEditClick(row: SegmentHistoryRecord): Promise<void> {
    if (this.isEditDisabled(row)) {
      this.messageService.warning('Generated segment results cannot be edited.');
      return;
    }

    try {
      const details = await this.fanExplorationService.fetchEditQueryDetails(row.queryId, row.executionId);

      if (!details) {
        this.messageService.warning('Unable to load segment details for edit.');
        return;
      }

      const queryType = this.fanExplorationService.getEditQueryType(details);
      this.router.navigate([`/market-fan-exploration/edit/${queryType}/${row.queryId}/${row.executionId}`]);
    } catch (error) {
      console.error('Failed to load segment edit details', error);
      this.messageService.error('Unable to open segment edit view. Please try again.');
    }
  }

  async openViewSegmentModal(row: SegmentHistoryRecord): Promise<void> {
    this.selectedRow = row;
    this.selectedQueryRecord = null;
    this.selectedQueryText = '';
    this.isViewModalLoading = true;
    this.isViewModalVisible = true;

    if (!row.queryId || !row.executionId) {
      this.selectedQueryText = '-- Query text is not available for this segment execution.';
      this.isViewModalLoading = false;
      return;
    }

    try {
      const queryRecord = await this.queryHistoryService.getQueryRecordById(row.queryId, row.executionId);

      if (this.selectedRow?.queryId !== row.queryId || this.selectedRow?.executionId !== row.executionId) {
        return;
      }

      this.selectedQueryRecord = queryRecord;
      this.selectedQueryText =
        queryRecord?.query?.trim() || '-- Query text is not available for this segment execution.';
    } catch (error) {
      console.error('Failed to load segment query details', error);

      if (this.selectedRow?.queryId === row.queryId && this.selectedRow?.executionId === row.executionId) {
        this.selectedQueryText = '-- Failed to load query text.';
      }
    } finally {
      if (this.selectedRow?.queryId === row.queryId && this.selectedRow?.executionId === row.executionId) {
        this.isViewModalLoading = false;
      }
    }
  }

  closeViewSegmentModal(): void {
    this.isViewModalVisible = false;
    this.isViewModalLoading = false;
    this.selectedRow = null;
    this.selectedQueryRecord = null;
    this.selectedQueryText = '';
  }

  private matchesSearch(row: SegmentHistoryRecord): boolean {
    const searchValue = this.searchTerm.trim().toLowerCase();

    if (!searchValue) {
      return true;
    }

    return [
      row.queryId,
      row.segmentName,
      row.segmentDescription,
      String(row.segmentedFans),
      row.createdOn,
      row.createdBy,
    ].some((value) => value.toLowerCase().includes(searchValue));
  }

  delete(row: SegmentHistoryRecord): void {
    this.deleteEvent.emit(row);
  }

  getStatusColor(status: QueryStatus): string {
    return this.queryHistoryService.getStatusColor(status);
  }

  getDisplayStatus(status: QueryStatus): string {
    return this.queryHistoryService.getDisplayStatus(status);
  }
}
